import { WSPageToolbarAction } from "@wingspanhq/fe-component-library";
import { BackgroundCheck } from "@wingspanhq/wingspan-api-sdk";

import { openInNewTab } from "../../../../../../shared/utils/openInNewTab";
import { getRequirementDefinitionTableCellProps } from "../../../../../RequirementDefinitions/utils";
import {
  CellsConfig,
  ContextItem,
  RequirementTableCallback,
  RequirementTableItemData
} from "../../../../types";
import { getRequirementEngagementsTableCellProps } from "../../../../utils";
import { ReportsInfoWidget } from "../../components/ReportsInfoWidget";
import { getBackgroundCheckPayerRequirementStatus } from "../helpers/getBackgroundCheckPayerRequirementStatus";
import { getBackgroundCheckProgressSteps } from "../helpers/getBackgroundCheckProgressSteps";
import { getBackgroundChecksRequirementName } from "../helpers/getBackgroundChecksRequirementName";

import { payeeBackgroundCheckRenderCardDescription } from "./payeeBackgroundCheckRenderCardDescription";

export const usePayeeBackgroundChecksConfig = (
  callback: RequirementTableCallback
): CellsConfig<
  RequirementTableItemData,
  ContextItem,
  BackgroundCheck | undefined
> => {
  const getManageReportAction = (
    requirement: RequirementTableItemData,
    context: ContextItem,
    backgroundCheck: BackgroundCheck | undefined
  ) => {
    if (backgroundCheck?.manageReportUrl) {
      return async () => {
        openInNewTab(backgroundCheck.manageReportUrl!);
      };
    }
  };

  const getOpenReportAction = (
    requirement: RequirementTableItemData,
    context: ContextItem,
    backgroundCheck: BackgroundCheck | undefined
  ) => {
    if (backgroundCheck?.viewReportUrl) {
      return async () => {
        openInNewTab(backgroundCheck.viewReportUrl!);
      };
    }
  };

  return {
    isActionRequired: () => {
      return false;
    },
    engagementsCell: getRequirementEngagementsTableCellProps,
    nameCell: (requirement, context, backgroundCheck) => {
      return {
        text: getBackgroundChecksRequirementName(
          requirement,
          context,
          backgroundCheck
        )
      };
    },
    requirementDetails: (requirement, context, backgroundCheck) => {
      const manageReportAction = getManageReportAction(
        requirement,
        context,
        backgroundCheck
      );

      const openReportAction = getOpenReportAction(
        requirement,
        context,
        backgroundCheck
      );

      return {
        actions:
          manageReportAction || openReportAction
            ? {
                alignment: "fill",
                buttons: [
                  {
                    label: "Manage report",
                    kind: "Secondary",
                    onAsyncClick: manageReportAction,
                    visible: !!manageReportAction
                  },
                  {
                    label: "View report",
                    kind: "Secondary",
                    onAsyncClick: openReportAction,
                    visible: !!openReportAction
                  }
                ]
              }
            : undefined,
        children: payeeBackgroundCheckRenderCardDescription(
          requirement,
          backgroundCheck
        ),
        data: [],
        header: {
          label: {
            text: getBackgroundChecksRequirementName(
              requirement,
              context,
              backgroundCheck
            )
          }
        }
      };
    },
    requirementDetailsProgressWidget: (
      requirement,
      context,
      backgroundCheck
    ) => {
      return backgroundCheck?.status
        ? getBackgroundCheckProgressSteps(backgroundCheck.status)
        : undefined;
    },
    requirementDetailsWidgets: (requirement, context, backgroundCheck) => {
      if (backgroundCheck) {
        return <ReportsInfoWidget backgroundCheck={backgroundCheck} />;
      }

      return null;
    },
    requirementName: getBackgroundChecksRequirementName,
    requirementStatusAlert: () => {
      return undefined;
    },
    statusCell: (requirement, context, backgroundCheck) => {
      return getBackgroundCheckPayerRequirementStatus(
        requirement,
        context,
        backgroundCheck
      );
    },
    toolbarActions: (requirement, context, backgroundCheck) => {
      const actions: WSPageToolbarAction[] = [];

      const manageReportAction = getManageReportAction(
        requirement,
        context,
        backgroundCheck
      );

      const openReportAction = getOpenReportAction(
        requirement,
        context,
        backgroundCheck
      );

      if (manageReportAction) {
        actions.push({
          buttonKind: "Secondary",
          icon: "open-tab",
          label: "Manage report",
          name: "Manage",
          onAsyncClick: manageReportAction
        });
      }

      if (openReportAction) {
        actions.push({
          buttonKind: "Secondary",
          icon: "open-tab",
          label: "View report",
          name: "View",
          onAsyncClick: openReportAction
        });
      }

      return actions;
    },
    typeCell: requirement =>
      getRequirementDefinitionTableCellProps({
        type: requirement.type
      })
  };
};
